import React, { useEffect, useState } from 'react';
import { Card, Select, Spin } from 'antd';
import type { UserCreate, UserUpdate } from '../types/user.types';
import { useUserForm } from '../hooks/use-user-form.hook';
import { useRoleList } from '../../roles/hooks/use-role.hook';
import FormBuilder, { FormField } from '../../../../common/FormBuilder';
import { useTranslation } from 'react-i18next';
import { DefaultValues } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { Input } from 'antd';
import { Auth0IdentityProvider } from '@gluai-studio/passport';
import SelectField from '@components/common/SelectField';
import PhoneNumberInput from '../../../../common/PhoneNumberInput';
import { phoneService } from '../../../../../services/phone.service';

interface UserFormProps {
  defaultValues?: DefaultValues<UserCreate | UserUpdate>;
  onFormSubmit: (data: UserCreate | UserUpdate, resetForm: () => void) => void;
  isLoading: boolean;
  columns?: 1 | 2 | 3 | 4;
  validationErrors?: ValidationError[];
}

function UserFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  columns = 2,
  validationErrors = [],
}: UserFormProps) {
  const { t } = useTranslation();
  const [formFields, setFormFields] = useState<FormField<UserCreate | UserUpdate>[]>([]);

  const { control, errors, resetForm, formSubmitHandler, reset, setError, setValue } = useUserForm({
    defaultValues,
    onSubmit: (data) => onFormSubmit(data, resetForm),
  });

  // Reset form values when defaultValues change
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  // Handle API validation errors
  useEffect(() => {
    validationErrors.forEach((error) => {
      setError(error.path as any, {
        type: 'manual',
        message: error.message,
      });
    });
  }, [validationErrors, setError]);

  // Manage form fields based on defaultValues
  useEffect(() => {
    const roleField: FormField<UserCreate | UserUpdate> = {
      name: 'role',
      label: t('users.role.label'),
      required: true,
      errorMsg: errors?.role?.message as string | undefined,
      render: (field) => (
        console.log('field', field),
        (
          <SelectField
            {...field}
            disabled={isLoading}
            placeholder={t('users.role.placeholder')}
            useListHook={useRoleList}
            labelKey="name"
            valueKey="id"
          />
        )
      ),
    };

    const allFields: FormField<UserCreate | UserUpdate>[] = [
      {
        name: 'name',
        label: t('users.name.label'),
        required: true,
        errorMsg: errors?.name?.message as string | undefined,
        rules: {
          required: 'Name is required',
          minLength: { value: 3, message: 'Name must be at least 3 characters' },
          maxLength: { value: 50, message: 'Name must be at most 50 characters' },
        },
        render: (field) => (
          <Input {...field} disabled={isLoading} placeholder={t('users.name.placeholder')} />
        ),
      },
      {
        name: 'email',
        label: t('users.email.label'),
        required: true,
        errorMsg: errors?.email?.message as string | undefined,
        rules: { required: 'Email is required' },
        render: (field) => (
          <Input {...field} disabled={isLoading} placeholder={t('users.email.placeholder')} />
        ),
      },
      {
        name: 'contact',
        label: t('users.contact.label'),
        required: false,
        errorMsg: errors?.contact?.message as string | undefined,
        rules: {
          validate: (value: string) =>
            !value || phoneService.isValid(value) || 'Invalid phone number',
        },
        render: (field) => (
          <PhoneNumberInput
            {...field}
            disabled={isLoading}
            placeholder={t('users.contact.placeholder')}
            defaultCountryCode="AE"
            invalidMessage={errors?.contact?.message || 'Invalid phone number'}
          />
        ),
      },
      {
        name: 'userId',
        label: t('users.userId.label'),
        required: false,
        errorMsg: errors?.userId?.message as string | undefined,
        render: (field) => (
          <Input {...field} disabled={isLoading} placeholder={t('users.userId.placeholder')} />
        ),
      },
      {
        name: 'userType',
        label: t('users.userType.label'),
        required: true,
        errorMsg: errors?.userType?.message as string | undefined,
        render: (field) => (
          <Select {...field} disabled={isLoading} placeholder={t('users.userType.placeholder')}>
            <Select.Option value={Auth0IdentityProvider.DATABASE}>
              {t('users.userType.database')}
            </Select.Option>
            <Select.Option value={Auth0IdentityProvider.GOOGLE}>
              {t('users.userType.google')}
            </Select.Option>
          </Select>
        ),
      },
      roleField,
    ];

    setFormFields(defaultValues ? [roleField] : allFields);
  }, [defaultValues, isLoading, t, errors]);

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <FormBuilder
          formFields={formFields}
          control={control}
          errors={errors}
          onSubmit={formSubmitHandler()}
          submitButtonText={t('common.save')}
          isLoading={isLoading}
          columns={columns}
        />
      </Card>
    </Spin>
  );
}

// Custom comparison function for memoization
function areEqual(prevProps: UserFormProps, nextProps: UserFormProps): boolean {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.onFormSubmit === nextProps.onFormSubmit &&
    prevProps.columns === nextProps.columns &&
    JSON.stringify(prevProps.defaultValues) === JSON.stringify(nextProps.defaultValues) &&
    JSON.stringify(prevProps.validationErrors) === JSON.stringify(nextProps.validationErrors)
  );
}

export default React.memo(UserFormComponent, areEqual);
